/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { Capacitor } from '@capacitor/core';

/**
 * Backup File Handler
 * Handles platform-specific file download/upload operations
 * 
 * Web: Uses browser Blob + download link
 * Native: Uses Capacitor Filesystem API (to be implemented)
 */
class BackupFileHandler {
  /**
   * Download backup JSON file
   * @param {Object} backupData - Backup data object
   * @param {string} filename - Filename for download
   * @returns {Promise<void>}
   */
  async downloadBackup(backupData, filename) {
    const platform = Capacitor.getPlatform();
    
    if (platform === 'web') {
      await this._downloadWeb(backupData, filename);
    } else {
      await this._downloadNative(backupData, filename);
    }
  }

  /**
   * Upload/read backup JSON file
   * @returns {Promise<Object>} Parsed backup data
   */
  async uploadBackup() {
    const platform = Capacitor.getPlatform();
    
    if (platform === 'web') {
      return await this._uploadWeb();
    } else {
      return await this._uploadNative();
    }
  }

  /**
   * Download backup file on web platform
   * @private
   * @param {Object} backupData - Backup data
   * @param {string} filename - Filename
   */
  async _downloadWeb(backupData, filename) {
    try {
      // Convert to JSON string with formatting
      const jsonString = JSON.stringify(backupData, null, 2);
      
      // Create blob
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log(`Backup downloaded: ${filename}`);
    } catch (error) {
      console.error('Web download failed:', error);
      throw new Error(`Failed to download backup: ${error.message}`);
    }
  }

  /**
   * Upload backup file on web platform
   * @private
   * @returns {Promise<Object>} Parsed backup data
   */
  async _uploadWeb() {
    return new Promise((resolve, reject) => {
      try {
        // Create file input element
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'application/json,.json';
        
        input.onchange = async (event) => {
          try {
            const file = event.target.files[0];
            
            if (!file) {
              reject(new Error('No file selected'));
              return;
            }
            
            // Read file content
            const text = await file.text();
            
            // Parse JSON
            const backupData = JSON.parse(text);
            
            console.log(`Backup file loaded: ${file.name}`);
            resolve(backupData);
          } catch (error) {
            if (error instanceof SyntaxError) {
              reject(new Error('Invalid backup file: File is not valid JSON'));
            } else {
              reject(new Error(`Failed to read backup file: ${error.message}`));
            }
          }
        };
        
        input.oncancel = () => {
          reject(new Error('File selection cancelled'));
        };
        
        // Trigger file picker
        input.click();
      } catch (error) {
        reject(new Error(`Failed to open file picker: ${error.message}`));
      }
    });
  }

  /**
   * Download backup file on native platforms
   * @private
   * @param {Object} backupData - Backup data
   * @param {string} filename - Filename
   */
  async _downloadNative(backupData, filename) {
    // TODO: Implement native file download using @capacitor/filesystem
    // For Phase 1, we'll focus on web implementation
    throw new Error('Native backup download not yet implemented. Please use web version for now.');
    
    /* Future implementation:
    import { Filesystem, Directory } from '@capacitor/filesystem';
    
    const jsonString = JSON.stringify(backupData, null, 2);
    
    await Filesystem.writeFile({
      path: filename,
      data: jsonString,
      directory: Directory.Documents,
      encoding: Encoding.UTF8
    });
    */
  }

  /**
   * Upload backup file on native platforms
   * @private
   * @returns {Promise<Object>} Parsed backup data
   */
  async _uploadNative() {
    // TODO: Implement native file upload using @capacitor/filesystem
    // For Phase 1, we'll focus on web implementation
    throw new Error('Native backup upload not yet implemented. Please use web version for now.');
    
    /* Future implementation:
    import { Filesystem, Directory } from '@capacitor/filesystem';
    
    const contents = await Filesystem.readFile({
      path: filename,
      directory: Directory.Documents,
      encoding: Encoding.UTF8
    });
    
    return JSON.parse(contents.data);
    */
  }
}

// Export singleton instance
export const backupFileHandler = new BackupFileHandler();
